import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Label } from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import FileUpload from '../DashboardProject/FileUpload';
import { commissionRate, calculateContractorAmount, calculateHomeownerAmount } from '../DashboardProject/BidPriceLogic';
import BidStatistics from '../DashboardProject/BidShared';
import { Address } from '../../models/Address.interface';
import { Bid, CostBreakdownItem } from '../DashboardProject/BidShared';
import { Attachment } from '../../models/Attachment.interface';
import { useUser } from '../../hooks';
import LoadingOverlay from '../../Components/LoadingOverlay';

// Import the new modular components
import ContractorInformationForm from './ContractorInformationForm';
import BidDetailsForm from './BidDetailsForm';
import BidPreviewModal from './BidPreviewModal';

interface PaymentScheduleItem {
  milestoneSequence?: number;
  condition: string;
  percentage: number;
  isLocked: boolean;
}

interface ContractorBidSubmissionProps {
  jobId: string;
  bidId?: string;
  onBidSubmit: () => void;
}

const ContractorBidSubmission: React.FC<ContractorBidSubmissionProps> = ({ jobId, bidId, onBidSubmit }) => {
  // Basic bid information
  const [bidType, setBidType] = useState('fixedPrice');
  const [bidAmount, setBidAmount] = useState('');
  const [bidAmountContractor, setBidAmountContractor] = useState('');
  const [fixedPriceType, setFixedPriceType] = useState('laborAndMaterials');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [approach, setApproach] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([]);
  
  // Modal states
  const [previewModal, setPreviewModal] = useState(false);
  const [submitModal, setSubmitModal] = useState(false);
  
  // Loading and alert states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  // User context
  const { currentUserId, contractor, contractorId } = useUser();

  // Contractor information
  const [contractorName, setContractorName] = useState('');
  const [contractorLicense, setContractorLicense] = useState('');
  const [contractorAddress, setContractorAddress] = useState<Address>({
    address1: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [contractorPhone, setContractorPhone] = useState('');
  const [contractorEmail, setContractorEmail] = useState('');
  const [warrantyPeriod, setWarrantyPeriod] = useState('365');
  const [terminationNoticeDays, setTerminationNoticeDays] = useState('');
  const [delayDays, setDelayDays] = useState('');

  // Payment schedule and cost breakdown
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentScheduleItem[]>([
    { milestoneSequence: 2, condition: "Post-contract deposit (held in escrow)", percentage: 50, isLocked: true },
    { milestoneSequence: 21, condition: "Work complete final payment", percentage: 50, isLocked: false },
  ]);

  const [costBreakdown, setCostBreakdown] = useState<CostBreakdownItem[]>([
    { description: 'Labor', contractorAmount: '0', homeownerAmount: '0' },
    { description: 'Materials', contractorAmount: '0', homeownerAmount: '0' },
  ]);

  const [isCostBreakdownValid, setIsCostBreakdownValid] = useState(true);
  const [requireInspection, setRequireInspection] = useState(false);

  // Payment schedule templates
  const STANDARD_INSPECTION_SCHEDULE: PaymentScheduleItem[] = [
    {
      milestoneSequence: 2,
      condition: "Post-contract deposit (held in escrow)",
      percentage: 50,
      isLocked: true
    },
    {
      milestoneSequence: 21,
      condition: "Work complete final payment",
      percentage: 50,
      isLocked: false
    }
  ];

  const REQUIRED_INSPECTION_SCHEDULE: PaymentScheduleItem[] = [
    {
      milestoneSequence: 2,
      condition: "Post-contract deposit (held in escrow)",
      percentage: 25,
      isLocked: true
    },
    {
      milestoneSequence: 11,
      condition: "Pre-work deposit (held in escrow)",
      percentage: 25,
      isLocked: true
    },
    {
      milestoneSequence: 21,
      condition: "Work complete final payment",
      percentage: 50,
      isLocked: false
    }
  ];

  const requireInspectionSetPaymentSchedule = () => {
    const schedule = requireInspection ? REQUIRED_INSPECTION_SCHEDULE : STANDARD_INSPECTION_SCHEDULE;
    setPaymentSchedule(schedule);
  };

  // Send notification after bid submission
  const sendBidSubmissionNotifications = async (bidData: any, jobData: any) => {
    try {
      const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
      
      // Ensure we have all required data and no undefined values
      const cleanBidData = {
        ...bidData,
        // Ensure all required fields are present and not undefined
        contractorName: bidData.contractorName || '',
        contractorEmail: bidData.contractorEmail || '',
        contractorPhone: bidData.contractorPhone || '',
        contractorLicense: bidData.contractorLicense || '',
        contractorAddress: bidData.contractorAddress || {
          address1: '',
          city: '',
          state: '',
          zipCode: ''
        }
      };

      const cleanContractorData = {
        ...contractor,
        // Ensure contractor data has required fields
        businessName: contractor?.businessName || '',
        email: contractor?.email || '',
        phone: contractor?.phone || '',
        contractorId: contractor?.contractorId || contractorId || ''
      };
      
      // Send notifications to both contractor and customer
      await fetch(`${serverUrl}/bid-submission-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          bidData: cleanBidData,
          jobData,
          contractorData: cleanContractorData
        }),
      });
      
      console.log('Bid submission notifications sent successfully');
    } catch (error) {
      console.error('Error sending bid submission notifications:', error);
      // Don't throw error - notification failure shouldn't break bid submission
    }
  };

  // Fetch existing bid data if editing
  const fetchBidData = async () => {
    if (bidId) {
      const firestore = firebase.firestore();
      try {
        const bidDoc = await firestore.collection('jobs').doc(jobId).collection('bids').doc(bidId).get();
        if (bidDoc.exists) {
          const data = bidDoc.data();
          if (data) {
            // Populate all form fields with existing data
            setBidType(data.bidType || 'fixedPrice');
            setBidAmount(data.bidAmount);
            setBidAmountContractor(data.bidAmountContractor);
            setFixedPriceType(data.fixedPriceType || 'laborAndMaterials');
            setStartDate(data.startDate || '');
            setEndDate(data.endDate || '');
            setApproach(data.approach || '');
            setCostBreakdown(data.costBreakdown || []);
            setPaymentSchedule(data.paymentSchedule || []);
            setRequireInspection(data.requireInspection || false);
            setWarrantyPeriod(data.warrantyPeriod?.toString() || '365');
            setTerminationNoticeDays(data.terminationNoticeDays?.toString() || '');
            setDelayDays(data.delayDays?.toString() || '');
            setContractorName(data.contractorName || '');
            setContractorLicense(data.contractorLicense || '');
            setContractorAddress(data.contractorAddress || {
              address1: '',
              city: '',
              state: '',
              zipCode: ''
            });
            setContractorPhone(data.contractorPhone || '');
            setContractorEmail(data.contractorEmail || '');

            // Set existing attachments - avoid duplicates
            const attachments = data.attachments || [];
            const uniqueAttachments = attachments.filter((attachment: string | Attachment, index: number) => {
              const url = typeof attachment === 'string' ? attachment : attachment.url;
              return attachments.findIndex((a: string | Attachment) => {
                const aUrl = typeof a === 'string' ? a : a.url;
                return aUrl === url;
              }) === index;
            });

            setExistingAttachments(uniqueAttachments.map((attachment: string | Attachment) => ({
              id: typeof attachment === 'string' 
                ? `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                : attachment.id || `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              url: typeof attachment === 'string' ? attachment : attachment.url,
              caption: typeof attachment === 'string' ? '' : attachment.caption || '',
              order: typeof attachment === 'string' ? 0 : attachment.order || 0
            })));
          }
        }
      } catch (error) {
        console.error('Error fetching bid data:', error);
      }
    }
    setIsLoading(false);
  };

  // Initialize form with contractor data
  useEffect(() => {

    if (contractor) {
      debugger;
      setContractorName(contractor?.businessName || '');
      setContractorAddress({
        address1: contractor?.address?.address1 || '',
        city: contractor?.address?.city || '',
        state: contractor?.address?.state || '',
        zipCode: contractor?.address?.zipCode || ''
      });
      setContractorPhone(contractor?.phone || '');
      setContractorEmail(contractor?.email || '');
    }
    fetchBidData();
  }, [jobId, bidId, contractor]);

  useEffect(() => {
    requireInspectionSetPaymentSchedule();
  }, [requireInspection]);

  // File handling - fixed to prevent duplicates
  const handleFilesChange = (newFiles: File[]) => {
    console.log('handleFilesChange called with:', newFiles);
    setFiles(newFiles);
  };

  const uploadFiles = async (bidId: string) => {
    if (files.length === 0) return [];
    
    const storage = firebase.storage();
    const uploadPromises = files.map(file => {
      const fileRef = storage.ref(`bids/${bidId}/${file.name}`);
      return fileRef.put(file).then(() => fileRef.getDownloadURL());
    });
    return Promise.all(uploadPromises);
  };

  // Payment schedule handlers
  const handlePaymentScheduleChange = (index: number, field: 'condition' | 'percentage', value: string | number) => {
    const updatedSchedule = [...paymentSchedule];
    if (field === 'percentage') {
      updatedSchedule[index][field] = Number(value);
    } else {
      updatedSchedule[index][field] = value as string;
    }
    setPaymentSchedule(updatedSchedule);
  };

  const addPaymentScheduleItem = () => {
    setPaymentSchedule([...paymentSchedule, { condition: "", percentage: 0, isLocked: false }]);
  };

  const removePaymentScheduleItem = (index: number) => {
    const updatedSchedule = paymentSchedule.filter((_, i) => i !== index);
    setPaymentSchedule(updatedSchedule);
  };

  const validatePaymentSchedule = () => {
    const total = paymentSchedule.reduce((sum, item) => sum + item.percentage, 0);
    return Math.abs(total - 100) < 0.01;
  };

  // Cost breakdown handlers
  const updateCostBreakdown = (homeownerTotal: string, contractorTotal: string) => {
    const homeownerAmount = parseFloat(homeownerTotal) || 0;
    const contractorAmount = parseFloat(contractorTotal) || 0;

    setCostBreakdown(prevBreakdown => {
      const updatedBreakdown = [...prevBreakdown];
      if (updatedBreakdown.length >= 2) {
        updatedBreakdown[0] = {
          ...updatedBreakdown[0],
          contractorAmount: (contractorAmount / 2).toFixed(2),
          homeownerAmount: (homeownerAmount / 2).toFixed(2),
        };
        updatedBreakdown[1] = {
          ...updatedBreakdown[1],
          contractorAmount: (contractorAmount - parseFloat(updatedBreakdown[0].contractorAmount)).toFixed(2),
          homeownerAmount: (homeownerAmount - parseFloat(updatedBreakdown[0].homeownerAmount)).toFixed(2),
        };
      }
      return updatedBreakdown;
    });
  };

  const handleCostBreakdownChange = (index: number, field: keyof CostBreakdownItem, value: string) => {
    const updatedBreakdown = [...costBreakdown];
    updatedBreakdown[index][field] = value;

    if (field === 'contractorAmount') {
      updatedBreakdown[index].homeownerAmount = calculateHomeownerAmount(value);
    } else if (field === 'homeownerAmount') {
      updatedBreakdown[index].contractorAmount = calculateContractorAmount(value);
    }

    setCostBreakdown(updatedBreakdown);

    const newContractorTotal = updatedBreakdown.reduce((sum, item) => sum + parseFloat(item.contractorAmount || '0'), 0).toFixed(2);
    const newHomeownerTotal = updatedBreakdown.reduce((sum, item) => sum + parseFloat(item.homeownerAmount || '0'), 0).toFixed(2);

    setBidAmountContractor(newContractorTotal);
    setBidAmount(newHomeownerTotal);

    validateCostBreakdown(updatedBreakdown, newContractorTotal, newHomeownerTotal);
  };

  const addCostBreakdownItem = () => {
    setCostBreakdown([...costBreakdown, { description: '', contractorAmount: '0', homeownerAmount: '0' }]);
  };

  const removeCostBreakdownItem = (index: number) => {
    const updatedBreakdown = costBreakdown.filter((_, i) => i !== index);
    setCostBreakdown(updatedBreakdown);
  };

  const validateCostBreakdown = (
    breakdown: CostBreakdownItem[] = costBreakdown,
    contractorTotal: string = bidAmountContractor,
    homeownerTotal: string = bidAmount
  ) => {
    const breakdownContractorTotal = breakdown.reduce((sum, item) => sum + parseFloat(item.contractorAmount || '0'), 0);
    const breakdownHomeownerTotal = breakdown.reduce((sum, item) => sum + parseFloat(item.homeownerAmount || '0'), 0);

    const isValid =
      parseFloat(contractorTotal).toFixed(2) === breakdownContractorTotal.toFixed(2) &&
      parseFloat(homeownerTotal).toFixed(2) === breakdownHomeownerTotal.toFixed(2);

    setIsCostBreakdownValid(isValid);
    return isValid;
  };

  // Bid amount handlers
  const handleBidAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBidAmount(value);
    const contractorAmount = calculateContractorAmount(value);
    setBidAmountContractor(contractorAmount);
    updateCostBreakdown(value, contractorAmount);
  };

  const handleBidAmountContractorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBidAmountContractor(value);
    const homeownerAmount = calculateHomeownerAmount(value);
    setBidAmount(homeownerAmount);
    updateCostBreakdown(homeownerAmount, value);
  };

  // File deletion handler
  const handleExistingFileDelete = async (url: string) => {
    try {
      const storage = firebase.storage();
      await storage.refFromURL(url).delete();

      setExistingAttachments(prev => prev.filter(attachment => attachment.url !== url));

      if (bidId) {
        const firestore = firebase.firestore();
        await firestore.collection('jobs').doc(jobId).collection('bids').doc(bidId).update({
          attachments: existingAttachments.filter(attachment => attachment.url !== url)
        });
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // Preview handler
  const handlePreview = () => {
    setPreviewModal(true);
  };

  // Submit handler
  const handleConfirmSubmit = async () => {
    if (!validatePaymentSchedule()) {
      alert("Payment schedule percentages must total 100%.");
      return;
    }

    if (!validateCostBreakdown()) {
      alert("The sum of Cost Breakdown must equal the bid amount.");
      return;
    }

    setIsSubmitting(true);

    let bidRef: firebase.firestore.DocumentReference | null = null;
    const firestore = firebase.firestore();

    // Ensure all contractor fields have values (not undefined)
    const bidData = {
      bidType,
      bidAmount: parseFloat(bidAmount),
      bidAmountContractor: parseFloat(bidAmountContractor),
      fixedPriceType: bidType === 'fixedPrice' ? fixedPriceType : null,
      startDate,
      endDate,
      approach,
      costBreakdown,
      contractorId: contractorId || '',
      contractorName: contractorName || '',
      contractorLicense: contractorLicense || '',
      contractorAddress: {
        address1: contractorAddress.address1 || '',
        city: contractorAddress.city || '',
        state: contractorAddress.state || '',
        zipCode: contractorAddress.zipCode || '',
      },
      contractorPhone: contractorPhone || '',
      contractorEmail: contractorEmail || '',
      warrantyPeriod: warrantyPeriod || '365',
      terminationNoticeDays: terminationNoticeDays || '',
      delayDays: delayDays || '',
      status: 'Submitted',
      paymentSchedule,
      requireInspection,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    };

    try {
      if (bidId) {
        bidRef = firestore.collection('jobs').doc(jobId).collection('bids').doc(bidId);
        await bidRef.update(bidData);
      } else {
        const newBidRef = await firestore.collection('jobs').doc(jobId).collection('bids').add(bidData);
        bidRef = newBidRef;
      }

      if (bidRef) {
        // Upload new files
        const newFileUrls = await uploadFiles(bidRef.id);
        const newAttachments = newFileUrls.map((url, index) => ({
          id: `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          url,
          caption: '',
          order: existingAttachments.length + index
        }));

        // Combine existing and new attachments, ensuring no duplicates
        const allAttachments = [...existingAttachments];
        newAttachments.forEach(newAttachment => {
          const exists = allAttachments.some(existing => existing.url === newAttachment.url);
          if (!exists) {
            allAttachments.push(newAttachment);
          }
        });

        await bidRef.update({
          attachments: allAttachments,
        });

        // Get job data for notifications
        const jobDoc = await firestore.collection('jobs').doc(jobId).get();
        const jobData = jobDoc.exists ? { id: jobId, ...jobDoc.data() } : null;

        // Show success message for at least textInterval (1000ms)
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (jobData) {
          // Send notifications with clean data
          await sendBidSubmissionNotifications({
            ...bidData,
            id: bidRef.id,
            attachments: allAttachments
          }, jobData);
        }

        setAlertMessage({ type: 'success', message: bidId ? 'Bid updated successfully!' : 'Bid submitted successfully!' });
        setSubmitModal(false);
        onBidSubmit();
      } else {
        throw new Error('Failed to create or update bid');
      }
    } catch (error) {
      console.error('Error submitting bid:', error);
      setAlertMessage({ type: 'error', message: 'Error submitting bid. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <LoadingOverlay
        isVisible={isSubmitting}
        size="medium"
        text="BidBro"
        subtext="Saving your bid details & Notifying Customer..."
        debug={false}
      />
      
      <BidStatistics
        jobId={jobId}
        currentBidAmount={parseFloat(bidAmountContractor) || 0}
        isContractor={true}
      />
      <h2>Submit Your Bid</h2>

      <div>
        <ContractorInformationForm
          contractorName={contractorName}
          setContractorName={setContractorName}
          contractorLicense={contractorLicense}
          setContractorLicense={setContractorLicense}
          contractorAddress={contractorAddress}
          setContractorAddress={setContractorAddress}
          contractorPhone={contractorPhone}
          setContractorPhone={setContractorPhone}
          contractorEmail={contractorEmail}
          setContractorEmail={setContractorEmail}
        />

        <BidDetailsForm
          bidType={bidType}
          setBidType={setBidType}
          fixedPriceType={fixedPriceType}
          setFixedPriceType={setFixedPriceType}
          bidAmount={bidAmount}
          bidAmountContractor={bidAmountContractor}
          handleBidAmountChange={handleBidAmountChange}
          handleBidAmountContractorChange={handleBidAmountContractorChange}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          approach={approach}
          setApproach={setApproach}
          requireInspection={requireInspection}
          setRequireInspection={setRequireInspection}
          warrantyPeriod={warrantyPeriod}
          setWarrantyPeriod={setWarrantyPeriod}
          costBreakdown={costBreakdown}
          handleCostBreakdownChange={handleCostBreakdownChange}
          addCostBreakdownItem={addCostBreakdownItem}
          removeCostBreakdownItem={removeCostBreakdownItem}
          isCostBreakdownValid={isCostBreakdownValid}
          paymentSchedule={paymentSchedule}
          handlePaymentScheduleChange={handlePaymentScheduleChange}
          addPaymentScheduleItem={addPaymentScheduleItem}
          removePaymentScheduleItem={removePaymentScheduleItem}
          validatePaymentSchedule={validatePaymentSchedule}
          requireInspectionSetPaymentSchedule={requireInspectionSetPaymentSchedule}
        />

        <div className="mb-3 mt-5">
          <Label for="attachments">Attachments</Label>
          <FileUpload
            onFilesChange={handleFilesChange}
            existingFiles={existingAttachments}
            onExistingFileDelete={handleExistingFileDelete}
          />
        </div>

        <Button color="primary" onClick={handlePreview}>
          Preview Bid
        </Button>
      </div>

      {/* Preview Modal */}
      <BidPreviewModal
        isOpen={previewModal}
        toggle={() => setPreviewModal(!previewModal)}
        onSubmit={() => { setPreviewModal(false); setSubmitModal(true); }}
        contractorName={contractorName}
        contractorLicense={contractorLicense}
        contractorAddress={contractorAddress}
        contractorPhone={contractorPhone}
        contractorEmail={contractorEmail}
        bidType={bidType}
        fixedPriceType={fixedPriceType}
        bidAmountContractor={bidAmountContractor}
        bidAmount={bidAmount}
        startDate={startDate}
        endDate={endDate}
        approach={approach}
        warrantyPeriod={warrantyPeriod}
        requireInspection={requireInspection}
        costBreakdown={costBreakdown}
        paymentSchedule={paymentSchedule}
        files={files}
      />

      {/* Confirmation Modal */}
      <Modal isOpen={submitModal} toggle={() => setSubmitModal(!submitModal)}>
        <ModalHeader toggle={() => setSubmitModal(!submitModal)}>Confirm Submission</ModalHeader>
        <ModalBody>
          Are you sure you want to submit this bid?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSubmitModal(false)}>Cancel</Button>
          <Button color="primary" onClick={handleConfirmSubmit}>Confirm</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ContractorBidSubmission;